import type { Lang } from '../types';

export interface ReservationCheckinFormText {
  backToReservations: string;
  loading: string;
  errorLoadForm: string;
  // Page title / status badges
  guestFormTitle: string;
  formCompleted: string;
  formIncomplete: string;
  // Ubyport status badges
  ubyStatusSent: string;
  ubyStatusSending: string;
  ubyStatusPartial: string;
  ubyStatusFailed: string;
  ubyStatusUnavailable: string;
  ubyStatusNotSent: string;
  // Header meta
  guestsLabel: string;
  noEmail: string;
  formStarted: string;
  submittedManual: string;
  submittedAutoTrigger: string;
  submittedAutoSweep: string;
  submittedAutoRetry: string;
  ubyWarnPrefix: string;
  ubyDataErrorPrefix: string;
  ubyConnectivityPrefix: string;
  // Action buttons
  resendEmailBtn: string;
  resendingEmail: string;
  resetAllBtn: string;
  resettingAll: string;
  submitToUbyport: string;
  submittingAll: string;
  futureCheckinNote: string;
  slotsStillIncomplete: string;
  tooltipFutureCheckin: string;
  tooltipSlotsIncomplete: string;
  tooltipAlreadySent: string;
  // Slot card
  guestN: string;
  czCitizenBadge: string;
  slotComplete: string;
  slotEmpty: string;
  slotSentBadge: string;
  slotFailedBadge: string;
  /** A complete foreign guest the auto-send cap held back (#206): neutral, not a failure. */
  slotAwaitingManualBadge: string;
  /** Reservation badge for PARTIAL with no error text: nothing failed, guests are waiting for the manager. */
  ubyStatusPartialAwaiting: string;
  ubyAwaitingManualNote: (n: number) => string;
  ubyUnsentAfterSentNote: (n: number) => string;
  slotPendingBadge: string;
  noDataYet: string;
  ubyportErrorPrefix: string;
  ubyportNotePrefix: string;
  editBtn: string;
  resetSlotBtn: string;
  // Signature
  signatureLabel: string;
  signatureReadOnly: string;
  signatureNote: string;
  noSignature: string;
  // Edit form labels
  labelFirstName: string;
  labelSurname: string;
  labelDateOfBirth: string;
  dateOfBirthInvalid: string;
  labelPhone: string;
  labelAddress: string;
  labelCountry: string;
  selectCountry: string;
  czechCitizenNote: string;
  labelPurpose: string;
  selectPurpose: string;
  labelDocType: string;
  selectDocType: string;
  labelDocNumber: string;
  labelDocCountry: string;
  labelVisaNumber: string;
  labelResidencePermitNumber: string;
  disabilityCard: string;
  saveBtn: string;
  savingBtn: string;
  cancelBtn: string;
  // Document type labels
  docPassport: string;
  docIdCard: string;
  docDriverLicense: string;
  docGreenPassport: string;
  docGreenPassportEuResidence: string;
  // Field labels (read-only view)
  fieldFirstName: string;
  fieldSurname: string;
  fieldDateOfBirth: string;
  fieldPhone: string;
  fieldAddress: string;
  fieldCountry: string;
  fieldPurpose: string;
  fieldDocType: string;
  fieldDocNumber: string;
  fieldDocCountry: string;
  fieldVisaNumber: string;
  fieldResidencePermitNumber: string;
  // Reset result
  resetShareLink: string;
  copyLink: string;
  // Dorucenka
  dorucenkaIssued: string;
  stampLabel: string;
  downloadDorucenkaBtn: string;
  dorucenkasIssuedSuffix: string;
  downloadAllBtn: string;
  downloadBtn: string;
  // Confirm dialogs
  confirmResetSlotPre: string;
  confirmResetSlotPost: string;
  confirmResetAll: string;
  confirmSubmitAll: string;
  // Success messages
  successSaveSlot: string;
  successResetSlot: string;
  successResetAll: string;
  successResendEmailPre: string;
  successSubmitAll: string;
  // Error messages
  errorSaveSlot: string;
  errorResetSlot: string;
  errorResendEmail: string;
  errorSubmitAll: string;
}

export const reservationCheckinFormText: Record<Lang, ReservationCheckinFormText> = {
  cs: {
    backToReservations: '← Zpět na rezervace',
    loading: 'Načítám…',
    errorLoadForm: 'Nepodařilo se načíst formulář hosta. Ujistěte se, že tato rezervace patří vám.',
    guestFormTitle: 'Formulář hosta — ',
    formCompleted: 'Dokončeno',
    formIncomplete: 'Nedokončeno',
    ubyStatusSent: 'Odesláno do Ubyport',
    ubyStatusSending: 'Odesílám…',
    ubyStatusPartial: 'Částečně odesláno — chyba dat',
    ubyStatusFailed: 'Odeslání selhalo — chyba dat',
    ubyStatusUnavailable: 'Ubyport nedostupný — zkouším znovu…',
    ubyStatusNotSent: 'Neodesláno',
    guestsLabel: 'hostů',
    noEmail: 'Žádný e-mail',
    formStarted: 'Formulář zahájen: ',
    submittedManual: 'Odesláno ručně správcem',
    submittedAutoTrigger: 'Odesláno automaticky po dokončení formuláře',
    submittedAutoSweep: 'Odesláno automaticky denní kontrolou',
    submittedAutoRetry: 'Odesláno automaticky při opakování',
    ubyWarnPrefix: 'Upozornění: ',
    ubyDataErrorPrefix: 'Chyba dat — nutná ruční kontrola: ',
    ubyConnectivityPrefix: 'Chyba připojení — automaticky zkoušeno znovu: ',
    resendEmailBtn: '✉ Odeslat e-mail znovu',
    resendingEmail: 'Odesílám…',
    resetAllBtn: '⚠ Reset vše & nový odkaz',
    resettingAll: 'Resetuji…',
    submitToUbyport: 'Odeslat do Ubyport',
    submittingAll: 'Odesílám…',
    futureCheckinNote: 'Ubyport přijímá registrace pouze v den příjezdu nebo později',
    slotsStillIncomplete: 'slotů ještě nedokončeno',
    tooltipFutureCheckin: 'Ubyport přijímá registrace pouze v den příjezdu nebo později',
    tooltipSlotsIncomplete: 'Všechny sloty hostů musí být dokončeny před odesláním',
    tooltipAlreadySent: 'Již odesláno',
    guestN: 'Host ',
    czCitizenBadge: 'CZ občan',
    slotComplete: 'Dokončeno',
    slotEmpty: 'Prázdné',
    slotSentBadge: '✓ Odesláno',
    slotFailedBadge: '✗ Selhalo',
    slotAwaitingManualBadge: 'Čeká na ruční odeslání',
    ubyStatusPartialAwaiting: 'Částečně odesláno — zbytek čeká na vás',
    ubyAwaitingManualNote: (n) => `Automaticky nebylo odesláno hostů: ${n} (limit automatického odesílání). Zkontrolujte je a odešlete ručně.`,
    ubyUnsentAfterSentNote: (n) => `Hostů, kteří ještě nebyli odesláni na Ubyport: ${n} (formulář vyplnili až po odeslání ostatních). Odešlete je ručně.`,
    slotPendingBadge: 'Čeká',
    noDataYet: 'Zatím nebyla odeslána žádná data.',
    ubyportErrorPrefix: 'Chyba Ubyport: ',
    ubyportNotePrefix: 'Poznámka Ubyport: ',
    editBtn: 'Upravit',
    resetSlotBtn: 'Resetovat slot',
    signatureLabel: 'Podpis',
    signatureReadOnly: 'Podpis (pouze pro čtení)',
    signatureNote: 'Podpis byl poskytnut hostem a správce ho nemůže změnit.',
    noSignature: 'Žádný podpis',
    labelFirstName: 'Jméno *',
    labelSurname: 'Příjmení *',
    labelDateOfBirth: 'Datum narození',
    dateOfBirthInvalid: 'Datum narození musí být v minulosti (nejpozději včera).',
    labelPhone: 'Telefon',
    labelAddress: 'Adresa',
    labelCountry: 'Stát *',
    selectCountry: '— vyberte stát —',
    czechCitizenNote: 'Český občan — účel pobytu a podpis nejsou vyžadovány. Registrace Ubyport není potřeba.',
    labelPurpose: 'Účel pobytu *',
    selectPurpose: '— vyberte účel —',
    labelDocType: 'Typ dokladu *',
    selectDocType: '— vyberte —',
    labelDocNumber: 'Číslo dokladu *',
    labelDocCountry: 'Stát vydání dokladu *',
    labelVisaNumber: 'Číslo víza',
    labelResidencePermitNumber: 'Povolení k pobytu v EU/ČR',
    disabilityCard: 'Osoba s průkazem ZTP/P — platí snížená sazba místního poplatku',
    saveBtn: 'Uložit',
    savingBtn: 'Ukládám…',
    cancelBtn: 'Zrušit',
    docPassport: 'Cestovní pas',
    docIdCard: 'Občanský průkaz',
    docDriverLicense: 'Řidičský průkaz',
    docGreenPassport: 'Zelený pas',
    docGreenPassportEuResidence: 'Zelený pas s povolením k pobytu v EU/ČR',
    fieldFirstName: 'Jméno',
    fieldSurname: 'Příjmení',
    fieldDateOfBirth: 'Datum narození',
    fieldPhone: 'Telefon',
    fieldAddress: 'Adresa',
    fieldCountry: 'Stát',
    fieldPurpose: 'Účel pobytu',
    fieldDocType: 'Typ dokladu',
    fieldDocNumber: 'Číslo dokladu',
    fieldDocCountry: 'Stát vydání',
    fieldVisaNumber: 'Číslo víza',
    fieldResidencePermitNumber: 'Povolení k pobytu v EU/ČR',
    resetShareLink: '✅ Formulář resetován — sdílejte odkaz na check-in apartmánu s hostem:',
    copyLink: 'Kopírovat odkaz',
    dorucenkaIssued: 'Doručenka vydána',
    stampLabel: 'razítko: ',
    downloadDorucenkaBtn: 'Stáhnout doručenku PDF',
    dorucenkasIssuedSuffix: ' doručenek vydáno',
    downloadAllBtn: 'Stáhnout vše (ZIP)',
    downloadBtn: 'Stáhnout',
    confirmResetSlotPre: 'Smazat data pro ',
    confirmResetSlotPost: '? Host bude muset slot znovu vyplnit.',
    confirmResetAll: 'Resetovat VŠECHNY hosty?\n\nToto:\n• Smaže všechna odeslaná data hostů\n• Vymaže e-mail a odkaz na formulář\n• Host se musí znovu zaregistrovat přes odkaz na check-in apartmánu\n\nTuto akci nelze vrátit.',
    confirmSubmitAll: 'Odeslat všechny hosty do Ubyport? Před odesláním se ujistěte, že jsou všechny sloty dokončeny.',
    successSaveSlot: 'Informace o hostu aktualizovány.',
    successResetSlot: 'Slot resetován. Host ho může znovu vyplnit.',
    successResetAll: 'Všichni hosté resetováni. Host se může znovu zaregistrovat přes odkaz na check-in apartmánu.',
    successResendEmailPre: 'E-mail s odkazem na formulář odeslán znovu na ',
    successSubmitAll: 'Všichni hosté úspěšně odesláni do Ubyport.',
    errorSaveSlot: 'Nepodařilo se uložit změny. Zkuste to prosím znovu.',
    errorResetSlot: 'Nepodařilo se resetovat slot. Zkuste to prosím znovu.',
    errorResendEmail: 'Nepodařilo se znovu odeslat e-mail.',
    errorSubmitAll: 'Nepodařilo se odeslat do Ubyport.',
  },
  en: {
    backToReservations: '← Back to Reservations',
    loading: 'Loading…',
    errorLoadForm: 'Failed to load guest form. Make sure this reservation belongs to you.',
    guestFormTitle: 'Guest Form — ',
    formCompleted: 'Completed',
    formIncomplete: 'Incomplete',
    ubyStatusSent: 'Sent to Ubyport',
    ubyStatusSending: 'Sending…',
    ubyStatusPartial: 'Partially Sent — data error',
    ubyStatusFailed: 'Send Failed — data error',
    ubyStatusUnavailable: 'Ubyport unreachable — retrying…',
    ubyStatusNotSent: 'Not Sent',
    guestsLabel: 'guests',
    noEmail: 'No email on file',
    formStarted: 'Form started: ',
    submittedManual: 'Submitted manually by manager',
    submittedAutoTrigger: 'Submitted automatically on form completion',
    submittedAutoSweep: 'Submitted automatically by daily sweep',
    submittedAutoRetry: 'Submitted automatically by retry job',
    ubyWarnPrefix: 'Warning: ',
    ubyDataErrorPrefix: 'Data error — manual review required: ',
    ubyConnectivityPrefix: 'Connectivity error — will retry automatically: ',
    resendEmailBtn: '✉ Resend Email',
    resendingEmail: 'Sending…',
    resetAllBtn: '⚠ Reset All & New Link',
    resettingAll: 'Resetting…',
    submitToUbyport: 'Submit to Ubyport',
    submittingAll: 'Submitting…',
    futureCheckinNote: 'Ubyport only accepts registrations on check-in day or later',
    slotsStillIncomplete: 'slot(s) still incomplete',
    tooltipFutureCheckin: 'Ubyport only accepts registrations on check-in day or later',
    tooltipSlotsIncomplete: 'All guest slots must be complete before submitting',
    tooltipAlreadySent: 'Already submitted',
    guestN: 'Guest ',
    czCitizenBadge: 'CZ citizen',
    slotComplete: 'Complete',
    slotEmpty: 'Empty',
    slotSentBadge: '✓ Sent',
    slotFailedBadge: '✗ Failed',
    slotAwaitingManualBadge: 'Awaiting manual send',
    ubyStatusPartialAwaiting: 'Partially Sent — the rest wait for you',
    ubyAwaitingManualNote: (n) => `${n} guest${n === 1 ? ' was' : 's were'} not sent automatically (auto-send limit). Review and send them manually.`,
    ubyUnsentAfterSentNote: (n) => `${n} guest${n === 1 ? ' has' : 's have'} not been sent to Ubyport yet (filled in the form after the others were sent). Send them manually.`,
    slotPendingBadge: 'Pending',
    noDataYet: 'No data submitted yet.',
    ubyportErrorPrefix: 'Ubyport error: ',
    ubyportNotePrefix: 'Ubyport note: ',
    editBtn: 'Edit',
    resetSlotBtn: 'Reset Slot',
    signatureLabel: 'Signature',
    signatureReadOnly: 'Signature (read-only)',
    signatureNote: 'Signature was provided by the guest and cannot be changed by the manager.',
    noSignature: 'No signature on file',
    labelFirstName: 'First Name *',
    labelSurname: 'Surname *',
    labelDateOfBirth: 'Date of Birth',
    dateOfBirthInvalid: 'Date of birth must be in the past (yesterday at the latest).',
    labelPhone: 'Phone',
    labelAddress: 'Address',
    labelCountry: 'Country *',
    selectCountry: '— select country —',
    czechCitizenNote: 'Czech citizen — purpose of stay and signature are not required. Ubyport registration is not needed.',
    labelPurpose: 'Purpose of Stay *',
    selectPurpose: '— select purpose —',
    labelDocType: 'Document Type *',
    selectDocType: '— select —',
    labelDocNumber: 'Document Number *',
    labelDocCountry: 'Document Country *',
    labelVisaNumber: 'Visa Number',
    labelResidencePermitNumber: 'EU/CR Residence Permit',
    disabilityCard: 'Person with Disability Card (ZTP/P) — reduced city tax rate applies',
    saveBtn: 'Save',
    savingBtn: 'Saving…',
    cancelBtn: 'Cancel',
    docPassport: 'Passport',
    docIdCard: 'ID Card',
    docDriverLicense: "Driver's License",
    docGreenPassport: 'Green Passport',
    docGreenPassportEuResidence: 'Green Passport with EU/CR Residence Permit',
    fieldFirstName: 'First Name',
    fieldSurname: 'Surname',
    fieldDateOfBirth: 'Date of Birth',
    fieldPhone: 'Phone',
    fieldAddress: 'Address',
    fieldCountry: 'Country',
    fieldPurpose: 'Purpose of Stay',
    fieldDocType: 'Document Type',
    fieldDocNumber: 'Document Number',
    fieldDocCountry: 'Document Country',
    fieldVisaNumber: 'Visa Number',
    fieldResidencePermitNumber: 'EU/CR Residence Permit',
    resetShareLink: '✅ Form reset — share the apartment check-in link with the guest:',
    copyLink: 'Copy Link',
    dorucenkaIssued: 'Dorucenka issued',
    stampLabel: 'stamp: ',
    downloadDorucenkaBtn: 'Download Dorucenka PDF',
    dorucenkasIssuedSuffix: ' Dorucenkas issued',
    downloadAllBtn: 'Download All (ZIP)',
    downloadBtn: 'Download',
    confirmResetSlotPre: 'Delete data for ',
    confirmResetSlotPost: '? The guest will need to re-fill this slot.',
    confirmResetAll: 'Reset ALL guests?\n\nThis will:\n• Delete all submitted guest data\n• Clear the email and form link\n• Guest must re-register from the apartment check-in link\n\nThis cannot be undone.',
    confirmSubmitAll: 'Submit all guests to Ubyport? Make sure all slots are complete before proceeding.',
    successSaveSlot: 'Guest information updated.',
    successResetSlot: 'Slot reset. The guest can now re-fill it.',
    successResetAll: 'All guests reset. The guest can re-register via the apartment check-in link.',
    successResendEmailPre: 'Form link email resent to ',
    successSubmitAll: 'All guests submitted to Ubyport successfully.',
    errorSaveSlot: 'Failed to save changes. Please try again.',
    errorResetSlot: 'Failed to reset slot. Please try again.',
    errorResendEmail: 'Failed to resend email.',
    errorSubmitAll: 'Failed to submit to Ubyport.',
  },
  vi: {
    backToReservations: '← Quay lại đặt phòng',
    loading: 'Đang tải…',
    errorLoadForm: 'Không thể tải biểu mẫu khách. Hãy đảm bảo đặt phòng này thuộc về bạn.',
    guestFormTitle: 'Biểu mẫu khách — ',
    formCompleted: 'Hoàn thành',
    formIncomplete: 'Chưa hoàn thành',
    ubyStatusSent: 'Đã gửi tới Ubyport',
    ubyStatusSending: 'Đang gửi…',
    ubyStatusPartial: 'Gửi một phần — lỗi dữ liệu',
    ubyStatusFailed: 'Gửi thất bại — lỗi dữ liệu',
    ubyStatusUnavailable: 'Ubyport không khả dụng — đang thử lại…',
    ubyStatusNotSent: 'Chưa gửi',
    guestsLabel: 'khách',
    noEmail: 'Không có email',
    formStarted: 'Biểu mẫu bắt đầu: ',
    submittedManual: 'Gửi thủ công bởi quản lý',
    submittedAutoTrigger: 'Tự động gửi khi hoàn thành biểu mẫu',
    submittedAutoSweep: 'Tự động gửi bằng lịch quét hằng ngày',
    submittedAutoRetry: 'Tự động gửi khi thử lại',
    ubyWarnPrefix: 'Cảnh báo: ',
    ubyDataErrorPrefix: 'Lỗi dữ liệu — cần xem xét thủ công: ',
    ubyConnectivityPrefix: 'Lỗi kết nối — sẽ tự động thử lại: ',
    resendEmailBtn: '✉ Gửi lại email',
    resendingEmail: 'Đang gửi…',
    resetAllBtn: '⚠ Đặt lại tất cả & liên kết mới',
    resettingAll: 'Đang đặt lại…',
    submitToUbyport: 'Gửi tới Ubyport',
    submittingAll: 'Đang gửi…',
    futureCheckinNote: 'Ubyport chỉ chấp nhận đăng ký vào ngày nhận phòng hoặc sau đó',
    slotsStillIncomplete: 'ô vẫn chưa hoàn thành',
    tooltipFutureCheckin: 'Ubyport chỉ chấp nhận đăng ký vào ngày nhận phòng hoặc sau đó',
    tooltipSlotsIncomplete: 'Tất cả ô khách phải được hoàn thành trước khi gửi',
    tooltipAlreadySent: 'Đã gửi',
    guestN: 'Khách ',
    czCitizenBadge: 'Công dân CZ',
    slotComplete: 'Hoàn thành',
    slotEmpty: 'Trống',
    slotSentBadge: '✓ Đã gửi',
    slotFailedBadge: '✗ Thất bại',
    slotAwaitingManualBadge: 'Chờ gửi thủ công',
    ubyStatusPartialAwaiting: 'Đã gửi một phần — phần còn lại chờ bạn',
    ubyAwaitingManualNote: (n) => `${n} khách chưa được gửi tự động (giới hạn gửi tự động). Hãy kiểm tra và gửi thủ công.`,
    ubyUnsentAfterSentNote: (n) => `${n} khách chưa được gửi lên Ubyport (điền form sau khi những người khác đã được gửi). Hãy gửi thủ công.`,
    slotPendingBadge: 'Chờ',
    noDataYet: 'Chưa có dữ liệu nào được gửi.',
    ubyportErrorPrefix: 'Lỗi Ubyport: ',
    ubyportNotePrefix: 'Ghi chú Ubyport: ',
    editBtn: 'Sửa',
    resetSlotBtn: 'Đặt lại ô',
    signatureLabel: 'Chữ ký',
    signatureReadOnly: 'Chữ ký (chỉ đọc)',
    signatureNote: 'Chữ ký do khách cung cấp và quản lý không thể thay đổi.',
    noSignature: 'Không có chữ ký',
    labelFirstName: 'Tên *',
    labelSurname: 'Họ *',
    labelDateOfBirth: 'Ngày sinh',
    dateOfBirthInvalid: 'Ngày sinh phải ở quá khứ (muộn nhất là hôm qua).',
    labelPhone: 'Điện thoại',
    labelAddress: 'Địa chỉ',
    labelCountry: 'Quốc gia *',
    selectCountry: '— chọn quốc gia —',
    czechCitizenNote: 'Công dân Séc — mục đích lưu trú và chữ ký không bắt buộc. Không cần đăng ký Ubyport.',
    labelPurpose: 'Mục đích lưu trú *',
    selectPurpose: '— chọn mục đích —',
    labelDocType: 'Loại giấy tờ *',
    selectDocType: '— chọn —',
    labelDocNumber: 'Số giấy tờ *',
    labelDocCountry: 'Quốc gia cấp giấy tờ *',
    labelVisaNumber: 'Số visa',
    labelResidencePermitNumber: 'Giấy phép cư trú tại EU/CH Séc',
    disabilityCard: 'Người có thẻ ZTP/P — áp dụng thuế thành phố giảm',
    saveBtn: 'Lưu',
    savingBtn: 'Đang lưu…',
    cancelBtn: 'Hủy',
    docPassport: 'Hộ chiếu',
    docIdCard: 'Chứng minh thư',
    docDriverLicense: 'Bằng lái xe',
    docGreenPassport: 'Hộ chiếu xanh',
    docGreenPassportEuResidence: 'Hộ chiếu xanh kèm giấy phép cư trú EU/CH Séc',
    fieldFirstName: 'Tên',
    fieldSurname: 'Họ',
    fieldDateOfBirth: 'Ngày sinh',
    fieldPhone: 'Điện thoại',
    fieldAddress: 'Địa chỉ',
    fieldCountry: 'Quốc gia',
    fieldPurpose: 'Mục đích lưu trú',
    fieldDocType: 'Loại giấy tờ',
    fieldDocNumber: 'Số giấy tờ',
    fieldDocCountry: 'Quốc gia cấp',
    fieldVisaNumber: 'Số visa',
    fieldResidencePermitNumber: 'Giấy phép cư trú tại EU/CH Séc',
    resetShareLink: '✅ Biểu mẫu đã đặt lại — chia sẻ liên kết nhận phòng căn hộ với khách:',
    copyLink: 'Sao chép liên kết',
    dorucenkaIssued: 'Đã cấp Doručenka',
    stampLabel: 'con dấu: ',
    downloadDorucenkaBtn: 'Tải xuống Doručenka PDF',
    dorucenkasIssuedSuffix: ' Doručenka đã cấp',
    downloadAllBtn: 'Tải xuống tất cả (ZIP)',
    downloadBtn: 'Tải xuống',
    confirmResetSlotPre: 'Xóa dữ liệu của ',
    confirmResetSlotPost: '? Khách sẽ cần điền lại ô này.',
    confirmResetAll: 'Đặt lại TẤT CẢ khách?\n\nĐiều này sẽ:\n• Xóa tất cả dữ liệu khách đã gửi\n• Xóa email và liên kết biểu mẫu\n• Khách phải đăng ký lại qua liên kết nhận phòng căn hộ\n\nHành động này không thể hoàn tác.',
    confirmSubmitAll: 'Gửi tất cả khách tới Ubyport? Hãy đảm bảo tất cả ô đã hoàn thành trước khi tiếp tục.',
    successSaveSlot: 'Thông tin khách đã được cập nhật.',
    successResetSlot: 'Ô đã đặt lại. Khách có thể điền lại.',
    successResetAll: 'Tất cả khách đã đặt lại. Khách có thể đăng ký lại qua liên kết nhận phòng căn hộ.',
    successResendEmailPre: 'Đã gửi lại email liên kết biểu mẫu tới ',
    successSubmitAll: 'Đã gửi thành công tất cả khách tới Ubyport.',
    errorSaveSlot: 'Không thể lưu thay đổi. Vui lòng thử lại.',
    errorResetSlot: 'Không thể đặt lại ô. Vui lòng thử lại.',
    errorResendEmail: 'Không thể gửi lại email.',
    errorSubmitAll: 'Không thể gửi tới Ubyport.',
  },
  uk: {
    backToReservations: '← Назад до бронювань',
    loading: 'Завантаження…',
    errorLoadForm: 'Не вдалося завантажити форму гостя. Переконайтеся, що це бронювання належить вам.',
    guestFormTitle: 'Форма гостя — ',
    formCompleted: 'Завершено',
    formIncomplete: 'Незавершено',
    ubyStatusSent: 'Надіслано до Ubyport',
    ubyStatusSending: 'Надсилання…',
    ubyStatusPartial: 'Частково надіслано — помилка даних',
    ubyStatusFailed: 'Помилка надсилання — помилка даних',
    ubyStatusUnavailable: 'Ubyport недоступний — повторна спроба…',
    ubyStatusNotSent: 'Не надіслано',
    guestsLabel: 'гостей',
    noEmail: 'Немає email',
    formStarted: 'Форму розпочато: ',
    submittedManual: 'Надіслано вручну менеджером',
    submittedAutoTrigger: 'Автоматично надіслано після заповнення форми',
    submittedAutoSweep: 'Автоматично надіслано щоденним скануванням',
    submittedAutoRetry: 'Автоматично надіслано при повторній спробі',
    ubyWarnPrefix: 'Попередження: ',
    ubyDataErrorPrefix: 'Помилка даних — потрібна ручна перевірка: ',
    ubyConnectivityPrefix: 'Помилка підключення — автоматичний повтор: ',
    resendEmailBtn: '✉ Надіслати email знову',
    resendingEmail: 'Надсилання…',
    resetAllBtn: '⚠ Скинути всіх & нове посилання',
    resettingAll: 'Скидання…',
    submitToUbyport: 'Надіслати до Ubyport',
    submittingAll: 'Надсилання…',
    futureCheckinNote: 'Ubyport приймає реєстрації лише у день заїзду або пізніше',
    slotsStillIncomplete: 'слот(ів) ще незавершено',
    tooltipFutureCheckin: 'Ubyport приймає реєстрації лише у день заїзду або пізніше',
    tooltipSlotsIncomplete: 'Всі слоти гостей мають бути заповнені перед надсиланням',
    tooltipAlreadySent: 'Вже надіслано',
    guestN: 'Гість ',
    czCitizenBadge: 'Громадянин ЧР',
    slotComplete: 'Завершено',
    slotEmpty: 'Порожньо',
    slotSentBadge: '✓ Надіслано',
    slotFailedBadge: '✗ Помилка',
    slotAwaitingManualBadge: 'Очікує ручного надсилання',
    ubyStatusPartialAwaiting: 'Частково надіслано — решта чекає на вас',
    ubyAwaitingManualNote: (n) => `Гостей, не надісланих автоматично (ліміт автоматичного надсилання): ${n}. Перевірте їх і надішліть вручну.`,
    ubyUnsentAfterSentNote: (n) => `Гостей, ще не надісланих до Ubyport (заповнили форму після надсилання інших): ${n}. Надішліть їх вручну.`,
    slotPendingBadge: 'Очікує',
    noDataYet: 'Дані ще не надіслано.',
    ubyportErrorPrefix: 'Помилка Ubyport: ',
    ubyportNotePrefix: 'Примітка Ubyport: ',
    editBtn: 'Редагувати',
    resetSlotBtn: 'Скинути слот',
    signatureLabel: 'Підпис',
    signatureReadOnly: 'Підпис (лише для читання)',
    signatureNote: 'Підпис наданий гостем і не може бути змінений менеджером.',
    noSignature: 'Підпис відсутній',
    labelFirstName: "Ім'я *",
    labelSurname: 'Прізвище *',
    labelDateOfBirth: 'Дата народження',
    dateOfBirthInvalid: 'Дата народження має бути в минулому (щонайпізніше вчора).',
    labelPhone: 'Телефон',
    labelAddress: 'Адреса',
    labelCountry: 'Країна *',
    selectCountry: '— оберіть країну —',
    czechCitizenNote: "Громадянин Чехії — мета перебування та підпис не обов'язкові. Реєстрація в Ubyport не потрібна.",
    labelPurpose: 'Мета перебування *',
    selectPurpose: '— оберіть мету —',
    labelDocType: 'Тип документу *',
    selectDocType: '— оберіть —',
    labelDocNumber: 'Номер документу *',
    labelDocCountry: 'Країна видачі документу *',
    labelVisaNumber: 'Номер візи',
    labelResidencePermitNumber: 'Дозвіл на проживання в ЄС/Чехії',
    disabilityCard: 'Особа з карткою ZTP/P — застосовується знижена ставка міського збору',
    saveBtn: 'Зберегти',
    savingBtn: 'Збереження…',
    cancelBtn: 'Скасувати',
    docPassport: 'Закордонний паспорт',
    docIdCard: 'Посвідчення особи',
    docDriverLicense: 'Водійські права',
    docGreenPassport: 'Зелений паспорт',
    docGreenPassportEuResidence: 'Зелений паспорт з дозволом на проживання в ЄС/Чехії',
    fieldFirstName: "Ім'я",
    fieldSurname: 'Прізвище',
    fieldDateOfBirth: 'Дата народження',
    fieldPhone: 'Телефон',
    fieldAddress: 'Адреса',
    fieldCountry: 'Країна',
    fieldPurpose: 'Мета перебування',
    fieldDocType: 'Тип документу',
    fieldDocNumber: 'Номер документу',
    fieldDocCountry: 'Країна видачі',
    fieldVisaNumber: 'Номер візи',
    fieldResidencePermitNumber: 'Дозвіл на проживання в ЄС/Чехії',
    resetShareLink: '✅ Форму скинуто — поділіться посиланням на заїзд з гостем:',
    copyLink: 'Скопіювати посилання',
    dorucenkaIssued: 'Doručenka видана',
    stampLabel: 'штамп: ',
    downloadDorucenkaBtn: 'Завантажити Doručenka PDF',
    dorucenkasIssuedSuffix: ' Doručenka видано',
    downloadAllBtn: 'Завантажити все (ZIP)',
    downloadBtn: 'Завантажити',
    confirmResetSlotPre: 'Видалити дані для ',
    confirmResetSlotPost: '? Гість повинен буде повторно заповнити цей слот.',
    confirmResetAll: 'Скинути ВСІХ гостей?\n\nЦе:\n• Видалить всі надіслані дані гостей\n• Очистить email та посилання на форму\n• Гість повинен повторно зареєструватися через посилання на заїзд квартири\n\nЦю дію неможливо скасувати.',
    confirmSubmitAll: 'Надіслати всіх гостей до Ubyport? Переконайтеся, що всі слоти заповнені перед відправкою.',
    successSaveSlot: 'Інформацію про гостя оновлено.',
    successResetSlot: 'Слот скинуто. Гість може знову заповнити його.',
    successResetAll: 'Всіх гостей скинуто. Гість може повторно зареєструватися через посилання на заїзд квартири.',
    successResendEmailPre: 'Посилання на форму надіслано повторно на ',
    successSubmitAll: 'Всіх гостей успішно надіслано до Ubyport.',
    errorSaveSlot: 'Не вдалося зберегти зміни. Будь ласка, спробуйте знову.',
    errorResetSlot: 'Не вдалося скинути слот. Будь ласка, спробуйте знову.',
    errorResendEmail: 'Не вдалося повторно надіслати email.',
    errorSubmitAll: 'Не вдалося надіслати до Ubyport.',
  },
};
